import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  fetchRealGpsLocations,
  formatCoordinates,
  getStatusColor,
  getGPSStatus,
  calculateETA,
} from '../services/gpsTrackingService';
import { nextStopETA } from '../services/liveEtaService';
import { fmtDbTime } from '../utils/dbTime';
// __OFF_SHIFT_BOARD__ off-shift drivers grey at their last position from before sign-off.
import { fetchShiftStates, applyOffShift } from '../services/shiftStatusService';
import { OFF_SHIFT_COLOR } from '../utils/offShift';

function FleetMapModal({ onClose }) {
  const [trucks, setTrucks] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [nextEtas, setNextEtas] = useState({});
  const [etaLoading, setEtaLoading] = useState({});
  const [shiftKnown, setShiftKnown] = useState(false);

  const refresh = useCallback(async (truckList) => {
    const list = truckList || trucks;
    if (!list.length) return;
    const [gps, shifts] = await Promise.all([fetchRealGpsLocations(list), fetchShiftStates()]);
    // __OFF_SHIFT_BOARD__ shift data unreadable = the rows as before (nobody greyed).
    const locs = await applyOffShift(list, gps, shifts);
    setShiftKnown(!!shifts);
    setLocations(locs);
    setLastUpdate(new Date());
  }, [trucks]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('trucks')
          .select('*')
          .eq('is_active', true)
          .order('truck_number');
        if (error) throw error;
        const list = data || [];
        setTrucks(list);
        await refresh(list);
      } catch (e) {
        console.error('Fleet modal error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!trucks.length) return;
    const interval = setInterval(() => refresh(), 30000);
    return () => clearInterval(interval);
  }, [trucks, refresh]);

  async function loadNextEta(truckId) {
    if (!truckId) return;
    setEtaLoading(prev => ({ ...prev, [truckId]: true }));
    try {
      const res = await nextStopETA({ truckId });
      setNextEtas(prev => ({ ...prev, [truckId]: res }));
    } finally {
      setEtaLoading(prev => ({ ...prev, [truckId]: false }));
    }
  }

  // Recompute the selected truck's live next-stop ETA on select / after a GPS refresh
  useEffect(() => {
    // __OFF_SHIFT_BOARD__ no live ETA for a driver who is off shift.
    if (selectedTruck && !(locations[selectedTruck] && locations[selectedTruck].offShift)) loadNextEta(selectedTruck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck, lastUpdate]);

  const liveCount  = Object.values(locations).filter(l => l.status === 'LIVE').length;
  const staleCount = Object.values(locations).filter(l => l.status === 'STALE').length;
  const offCount   = Object.values(locations).filter(l => l.status === 'OFFLINE' || l.status === 'NO DATA').length;
  const offShiftCount = Object.values(locations).filter(l => l.offShift).length;

  const sel = selectedTruck ? locations[selectedTruck] : null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)', borderRadius: '10px',
        padding: '1.5rem', maxWidth: '900px', width: '100%',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>Fleet Tracking</h2>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
              <span style={{ color: '#16c79a' }}>🟢 {liveCount} live</span>
              <span style={{ color: '#f39c12' }}>🟡 {staleCount} stale</span>
              <span style={{ color: '#e74c3c' }}>🔴 {offCount} offline</span>
              {shiftKnown && (
                <span style={{ color: OFF_SHIFT_COLOR }}>⚪ {offShiftCount} off shift</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {lastUpdate && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            <button onClick={() => refresh()} style={{
              padding: '0.5rem 1rem', backgroundColor: 'var(--accent-blue)',
              color: 'var(--text-primary)', border: 'none', borderRadius: '5px',
              cursor: 'pointer', fontWeight: 'bold'
            }}>Refresh</button>
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: '#7f8c8d',
              color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer'
            }}>Close</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-primary)' }}>
              Loading fleet data...
            </div>
          ) : trucks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              No active trucks found.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {trucks.map(truck => {
                const loc = locations[truck.id];
                const isSelected = selectedTruck === truck.id;
                const off = !!(loc && loc.offShift);
                const statusColor = off ? OFF_SHIFT_COLOR : (loc ? getStatusColor(loc.status) : '#7f8c8d');

                return (
                  <div key={truck.id}
                    onClick={() => setSelectedTruck(isSelected ? null : truck.id)}
                    style={{
                      backgroundColor: isSelected ? 'var(--surface-2)' : 'var(--bg-primary)',
                      border: `2px solid ${isSelected ? statusColor : 'var(--border-default)'}`,
                      borderRadius: '8px', padding: '1rem', cursor: 'pointer',
                      transition: 'all 0.2s',
                      opacity: off && !isSelected ? 0.6 : 1,
                      filter: off ? 'grayscale(1)' : 'none'
                    }}
                  >
                    {/* Truck row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>🚛</span>
                        <div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1rem' }}>
                            Truck {truck.truck_number}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                            {truck.license_plate || 'No plate'}{truck.make ? ` · ${truck.make} ${truck.model || ''}`.trim() : ''}
                            {off && loc.driverName ? ` · ${loc.driverName}` : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          display: 'inline-block', padding: '0.2rem 0.6rem',
                          backgroundColor: statusColor + '22', color: statusColor,
                          borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold',
                          border: `1px solid ${statusColor}`
                        }}>
                          {loc ? `${loc.statusDot} ${loc.status}` : '⚫ NO DATA'}
                        </div>
                        {off && (
                          <div style={{ color: OFF_SHIFT_COLOR, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {loc.offText}
                          </div>
                        )}
                        {!off && loc?.ageMinutes !== null && loc?.ageMinutes !== undefined && (
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {loc.ageMinutes === 0 ? 'Just now' : `${loc.ageMinutes}m ago`}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isSelected && loc && (
                      <div style={{
                        marginTop: '1rem', paddingTop: '1rem',
                        borderTop: '1px solid var(--border-default)',
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem'
                      }}>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', letterSpacing: '1px' }}>LOCATION</div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                            {formatCoordinates(loc.lat, loc.lng)}
                          </div>
                          {loc.lat && loc.lng && (
                            <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}
                              target="_blank" rel="noreferrer"
                              style={{ color: 'var(--accent-primary)', fontSize: '0.75rem' }}
                              onClick={e => e.stopPropagation()}>
                              Open in Maps →
                            </a>
                          )}
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', letterSpacing: '1px' }}>SPEED</div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                            {loc.speed_kmh !== null ? `${Math.round(loc.speed_kmh)} km/h` : '—'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', letterSpacing: '1px' }}>LAST PING</div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                            {loc.timestamp ? fmtDbTime(loc.timestamp) : '—'}
                          </div>
                        </div>
                        {off && (
                          <div style={{ gridColumn: '1 / -1', color: OFF_SHIFT_COLOR, fontSize: '0.85rem' }}>
                            ⚪ {loc.offText}{loc.hasData ? ' (last known position before sign-off)' : ' — no position from before the sign-off'}. The driver's phone shares no location while signed off.
                          </div>
                        )}
                        {!off && loc.hasData && (
                          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-default)', paddingTop: '0.75rem' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', letterSpacing: '1px' }}>NEXT STOP (LIVE ETA)</div>
                            {etaLoading[truck.id] ? (
                              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Calculating…</div>
                            ) : nextEtas[truck.id] && nextEtas[truck.id].success ? (
                              <div style={{ marginTop: '0.25rem' }}>
                                {nextEtas[truck.id].stale && (<span style={{ color: '#f39c12', fontSize: '0.7rem', marginRight: '0.4rem' }}>GPS {nextEtas[truck.id].gps.ageMinutes}m old</span>)}
                                <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                  {nextEtas[truck.id].next.address ? nextEtas[truck.id].next.address.split(',')[0] : 'Stop'} · Load #{nextEtas[truck.id].next.loadNumber}
                                </span>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold', marginLeft: '0.5rem' }}>
                                  ~{nextEtas[truck.id].next.etaFormatted} ({nextEtas[truck.id].next.minutesFromNow}m)
                                </span>
                                {nextEtas[truck.id].next.deltaMinutes !== null && nextEtas[truck.id].next.deltaMinutes !== undefined && (
                                  <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: nextEtas[truck.id].next.deltaMinutes > 20 ? '#e74c3c' : nextEtas[truck.id].next.deltaMinutes > 5 ? '#f39c12' : '#16c79a' }}>
                                    {nextEtas[truck.id].next.deltaMinutes > 0 ? `${nextEtas[truck.id].next.deltaMinutes}m late` : nextEtas[truck.id].next.deltaMinutes < 0 ? `${-nextEtas[truck.id].next.deltaMinutes}m early` : 'on time'} vs plan
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                {nextEtas[truck.id] && nextEtas[truck.id].reason === 'no_remaining' ? 'No remaining stops'
                                  : nextEtas[truck.id] && nextEtas[truck.id].reason === 'no_loads' ? 'No active load'
                                  : nextEtas[truck.id] && nextEtas[truck.id].reason === 'gps_stale' ? nextEtas[truck.id].message
                                  : 'No live ETA available'}
                              </div>
                            )}
                          </div>
                        )}
                        {!off && !loc.hasData && (
                          <div style={{ gridColumn: '1 / -1', color: '#f39c12', fontSize: '0.85rem' }}>
                            ⚠ No GPS data received from this truck. Ensure the driver app is running and location permissions are granted.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={{
          marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-default)',
          display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: 'var(--text-muted)'
        }}>
          <span>🟢 Live — ping within 5 min</span>
          <span>🟡 Stale — ping 5–30 min ago</span>
          <span>🔴 Offline — no ping in 30+ min</span>
          <span>⚫ No Data — driver app not connected</span>
          <span>⚪ Off shift — signed off; shown at the last position before sign-off</span>
        </div>
      </div>
    </div>
  );
}

export default FleetMapModal;
